import { paymentGatewaysState } from '@/states/paymentGatewaysState';

export const buildPaymentGatewayCacheKey = (
  orderOrCheckoutId: string,
  amount: number,
  countryCode: string,
  shopperLocale: string,
) => `${orderOrCheckoutId}:${amount}:${countryCode}:${shopperLocale}`;

interface PaymentGatewayFetchSlot {
  generation: number;
  inFlight: Promise<boolean> | null;
}

const slots = new Map<string, PaymentGatewayFetchSlot>();
let latestRequestId = 0;

const offlinePaymentAvailability = new Map<string, Promise<boolean>>();

const isCheckoutId = (orderOrCheckoutId: string) => {
  try {
    return atob(orderOrCheckoutId).startsWith('Checkout:');
  } catch {
    return false;
  }
};

// Order ids are never cached: the orders service turns bank transfer off once a payment succeeds.
export const loadOfflinePaymentAvailability = (
  orderOrCheckoutId: string,
  amount: number,
  query: () => Promise<{ available: boolean; failed: boolean }>,
): Promise<boolean> => {
  if (!isCheckoutId(orderOrCheckoutId)) {
    return query().then(({ available }) => available);
  }

  const cacheKey = `${orderOrCheckoutId}:${amount}`;
  const cached = offlinePaymentAvailability.get(cacheKey);
  if (cached) {
    return cached;
  }

  const forget = () => {
    if (offlinePaymentAvailability.get(cacheKey) === request) {
      offlinePaymentAvailability.delete(cacheKey);
    }
  };
  const request = query().then(({ available, failed }) => {
    if (failed) forget();
    return available;
  });
  request.catch(forget);

  offlinePaymentAvailability.set(cacheKey, request);
  return request;
};

const getSlot = (cacheKey: string): PaymentGatewayFetchSlot => {
  const existing = slots.get(cacheKey);
  if (existing) {
    return existing;
  }

  const created: PaymentGatewayFetchSlot = { generation: 0, inFlight: null };
  slots.set(cacheKey, created);
  return created;
};

export const isPaymentGatewayCacheValid = (
  cacheKey: string,
  orderOrCheckoutId: string,
  amount: number,
) => {
  const entry = paymentGatewaysState.entries[cacheKey];
  return (
    !!entry &&
    entry.id === orderOrCheckoutId &&
    entry.amount === amount &&
    entry.configs.length > 0
  );
};

export const startPaymentGatewayFetch = (
  cacheKey: string,
  runFetch: (
    isCurrent: () => boolean,
    isLatest: () => boolean,
  ) => Promise<void>,
) => {
  const slot = getSlot(cacheKey);
  if (slot.inFlight) {
    return slot.inFlight;
  }

  const generation = ++slot.generation;
  const requestId = ++latestRequestId;
  const isCurrent = () => slot.generation === generation;
  const isLatest = () => requestId === latestRequestId;

  const promise = Promise.resolve()
    .then(() => runFetch(isCurrent, isLatest))
    .then(() => isCurrent())
    .finally(() => {
      if (slot.inFlight === promise) {
        slot.inFlight = null;
      }
    });

  slot.inFlight = promise;
  return promise;
};

export const resetPaymentGatewayInitializeCoordinator = () => {
  slots.clear();
  offlinePaymentAvailability.clear();
  latestRequestId = 0;
  paymentGatewaysState.entries = {};
};
